import { Link } from "react-router-dom";
import logo from "@/assets/logo_perch.svg";
import { Instagram, Facebook } from "lucide-react";
import { AppLink } from "@/components/AppLink";

// TikTok icon component since lucide doesn't have one
const TikTokIcon = ({ size = 20 }: { size?: number }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const navLinks = [
    { label: "Shop", to: "/plans" },
    { label: "About", to: "/why-perch-mobile" },
    { label: "Contact us", to: "/contact" },
    { label: "FAQ", to: "/faq" },
    { label: "My Account", to: "/login" },
    { label: "Affiliates", to: "/affiliates" },
  ];

  return (
    <footer 
      className="w-full flex justify-center"
      style={{ 
        background: '#000000',
      }}
    >
      <div 
        className="w-full px-4 md:px-8 lg:px-[76px]"
        style={{ 
          maxWidth: '1514px',
        }}
      >
        {/* Logo */}
        <div className="pt-10 lg:pt-[63px]">
          <Link to="/">
            <img 
              src={logo} 
              alt="Perch Mobile" 
              className="brightness-0 invert w-[150px] h-auto lg:w-[216px] lg:h-[95px]"
              style={{
                objectFit: 'contain',
              }}
            />
          </Link>
        </div>

        {/* Divider */}
        <div 
          className="border-t border-white/20 mt-10 lg:mt-[165px]"
        />

        {/* Main content row */}
        <div 
          className="flex flex-col gap-6 lg:gap-0 lg:flex-row lg:justify-between lg:items-center mt-6 lg:mt-[40px]"
        >
          {/* Navigation links */}
          <nav className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-0 sm:items-center">
            {navLinks.map((link, index) => (
              <div key={link.to} className="flex items-center">
                <Link 
                  to={link.to}
                  className="font-['Instrument_Sans'] font-semibold text-white hover:text-[#B0F18D] transition-colors text-base sm:text-lg lg:text-xl"
                  style={{
                    letterSpacing: '-0.05em',
                  }}
                >
                  {link.label}
                </Link>
                {index < navLinks.length - 1 && (
                  <span 
                    className="text-white/40 hidden sm:inline-flex items-center justify-center mx-2 lg:mx-3"
                  >
                    |
                  </span>
                )}
              </div>
            ))}
          </nav>

        </div>

        {/* Second Divider */}
        <div 
          className="border-t border-white/20 my-6"
        />

        {/* Bottom row */}
        <div className="flex flex-col gap-6 sm:flex-row sm:justify-between sm:items-center pb-8">
          {/* Social links */}
          <div className="flex items-center gap-4">
            <span className="font-inter text-white text-sm">Follow us</span>
            <div className="flex items-center gap-3">
              <a 
                href="https://www.instagram.com/perchmobile/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center text-white hover:text-[#B0F18D] hover:border-[#B0F18D] transition-colors"
                aria-label="Follow us on Instagram"
              >
                <Instagram size={16} />
              </a>
              <a 
                href="https://www.tiktok.com/@perch.mobile" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center text-white hover:text-[#B0F18D] hover:border-[#B0F18D] transition-colors"
                aria-label="Follow us on TikTok"
              >
                <TikTokIcon size={16} />
              </a>
              <a 
                href="https://www.facebook.com/perchmobile" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center text-white hover:text-[#B0F18D] hover:border-[#B0F18D] transition-colors"
                aria-label="Follow us on Facebook"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>

          {/* Legal links */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Link 
              to="/terms-and-conditions"
              className="font-inter text-white hover:text-[#B0F18D] transition-colors text-sm"
            >
              Terms & Conditions
            </Link>
            <span className="text-white/40">|</span>
            <Link 
              to="/privacy-policy"
              className="font-inter text-white hover:text-[#B0F18D] transition-colors text-sm"
            >
              Privacy Policy
            </Link>
            <span className="text-white/40">|</span>
            <AppLink 
              to="/activation-guide"
              className="font-inter text-white hover:text-[#B0F18D] transition-colors text-sm"
            >
              Activation Guide
            </AppLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
